import { useEffect, useState } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { push } from "connected-react-router";
import { getAcquisitionParameters } from "../selectors";
import { Actions as Acquisition } from "../store/ducks/acquisition";
import { Actions as Imagery } from "../store/ducks/imagery";
import { Redirect, Route } from "react-router-dom";

export const PREVIOUS = -1;
export const FIRST = 0;
export const NEXT = 1;
export const FINALIZE = 2;

// state.acquisition (satellite/AOI/period/availableDates) is shared by several modules
// (Shoreline, Bathymetry, Intertidal, CSqueeze, ShoreR, ShoreS all read or write it through
// the components under components/acquisition/* or via the Acquisition action creators), but
// it has no notion of "which module this data belongs to". Without this check, switching from
// one module to another mid-flow (e.g. via the browser's back/forward buttons, or a stepper
// button) can leave a LATER step rendering with a satellite/AOI/date list left over from the
// module the user was in before — stepAttendsRequirements only checks field presence, not
// which module set them.
export const ACQUISITION_MODULE_KEY = "acquisitionActiveModule";

/**
 * @constant FURTHEST_STEP_KEY_PREFIX
 * @description sessionStorage key prefix, one entry per module, holding the furthest step the user
 * has actually reached in that module's flow. Requirements alone cannot answer whether a step may
 * be opened: only some steps declare one, so a step that declares nothing - Bathymetry's band and
 * algorithm selection, CSqueeze's land cover - would be reachable straight from the first step as
 * soon as anything earlier was satisfied. Recording how far the flow has come closes those, and it
 * has to outlive the page: the result page unmounts the wizard, so component state would not
 * survive the trip back into the steps.
 */
export const FURTHEST_STEP_KEY_PREFIX = "acquisitionFurthestStep:";

/**
 * @constant ALL_STEPS_REACHED
 * @description Value written to a module's furthest-step entry to mean "every step", used by the
 * session importer: an imported session is a finished one, and its steps were never walked through,
 * so there is no step number to record.
 */
export const ALL_STEPS_REACHED = "all";
const SHARED_SESSION_KEYS = [
  "selectedSatellite",
  "typeSatellite",
  "selectedSatelliteIndex",
  "acquisitionCloudCoverage",
  "acquisitionFullImageList",
  "acquisitionImageSelection",
  "acquisitionLastSearchSignature",
  "intertidalFullImageList",
  "intertidalImageSelection",
  "intertidalLastSearchSignature",
  "intertidalPeriodData",
  "csqueezeSelectedDates",
  "csqueezeCloudCoverage",
  "csqueezeActiveTab",
  "csqueezeLandCoverShapes",
  "euscoastSelectedDates",
  "euscoastCloudCoverage",
  "euscoastActiveTab",
  "euscoastLandCoverShapes",
  "shoreSFullImageList",
  "shoreSImageSelection",
  "mepbayFullImageList",
  "mepbayImageSelection",
  "mepbayCloudCoverage",
  "mepbayLastSearchSignature",
];

/**
 * @constant IMAGE_CACHE_SUFFIXES
 * @description What names a session key that holds images already found, a choice made among
 * them, or the search they came from. Matched against the list above rather than writing those
 * keys out a second time, so a module that adds one is covered here too.
 * @memberof module:Shared
 */
const IMAGE_CACHE_SUFFIXES = /(FullImageList|ImageSelection|LastSearchSignature)$/;

/**
 * @function clearAcquisitionImageCache
 * @description Forgets every image list already found, and the signature of the search that
 * found it.
 * These lists are cached so that returning to a step does not re-query Earth Engine, and the
 * signature they are reused under is built from the area and the period alone. The satellite is
 * not in it, so picking a different one and keeping the area left the previous satellite's images
 * on screen - Landsat scenes listed under Sentinel - and nothing in the flow noticed. Whatever
 * invalidates a search has to call this, rather than each caller spelling out the keys.
 * The per-period variants are suffixed with `:module:index`, so each key is cleared by prefix as
 * well as exactly.
 * **Called by:
 * @see {AOIChooser} - when the area changes.
 * @see {SatelliteChooser} - when the satellite or its processing level changes.
 * @memberof module:Shared
 * @returns {void}
 */
export const clearAcquisitionImageCache = () => {
  const cached = SHARED_SESSION_KEYS.filter((key) => IMAGE_CACHE_SUFFIXES.test(key));
  Object.keys(window.sessionStorage).forEach((key) => {
    if (cached.some((name) => key === name || key.startsWith(`${name}:`))) {
      window.sessionStorage.removeItem(key);
    }
  });
};

/**
 * @function useStepNavigation
 * @description Custom hook for managing step-based navigation in acquisition.
 * Provides functions to navigate between steps (next, previous, specific step) 
 and handles URL updates via React Router.
 * 
 * @param {Object} match - React Router match object containing URL params and path
 * @param {Array} STEPS - Array of step configurations/components
 * @param {String} processingPath -> final path, the module's processing page
 * @param {String} algorithm -> custom algorithm implemented when the page changes
 * 
 * *Called by:
 * @see {AcquisitionPage} - Shoreline's acquisition page
 * 
 * @returns {Object} Navigation functions
 * @returns {Function} {}.configHook - Configures the steps functions to adapt to the selected module
 * @returns {Function} {}.navigate - Navigate by direction (NEXT, PREVIOUS, FIRST, FINALIZE)
 * @returns {Function} {}.navigateTo - Navigate to specific step index
 * @returns {Function} {}.renderStep - Renders component for the current step
 * @returns {Function} {}.extractPath - Extracts the base path from the current URL
 * @returns {Function} {}.stepAttendsRequirements - check if the step is valid and attends requirements
 * @returns {Function} {}.makeRoutes - Generates route components for each step in the acquisition process.
 */
export const useStepNavigations = (
  match,
  STEPS,
  resultPath = "/main/processing",
  custoAlgorithm = "",
  acquisitionParameters = getAcquisitionParameters
) => {
  const dispatch = useDispatch();
  const acquisitionData = useSelector(acquisitionParameters, shallowEqual);

  const moduleId = match.path.split("/")[2];
  const storedModuleId = window.sessionStorage.getItem(ACQUISITION_MODULE_KEY);
  const isModuleMismatch = Boolean(storedModuleId) && storedModuleId !== moduleId;

  const currentStep = match.params.step !== undefined ? match.params.step - 1 : 0;
  const furthestStepKey = `${FURTHEST_STEP_KEY_PREFIX}${moduleId}`;

  useEffect(() => {
    if (isModuleMismatch) {
      dispatch(Acquisition.reset());
      dispatch(Imagery.reset());
      try {
        const { Actions: CSqueeze } = require('../store/ducks/csqueeze');
        dispatch(CSqueeze.reset());
        dispatch(CSqueeze.resetImageList());
      } catch (e) {}
      try {
        const { Actions: EUSCoast } = require('../store/ducks/euscoast');
        dispatch(EUSCoast.reset());
        dispatch(EUSCoast.resetImageList());
      } catch (e) {}
      SHARED_SESSION_KEYS.forEach((key) => window.sessionStorage.removeItem(key));
      window.sessionStorage.removeItem(furthestStepKey);
    }
    window.sessionStorage.setItem(ACQUISITION_MODULE_KEY, moduleId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleId]);

  useEffect(() => {
    const stored = window.sessionStorage.getItem(furthestStepKey);
    if (stored === ALL_STEPS_REACHED) return;
    if (stored === null || currentStep > Number(stored)) {
      window.sessionStorage.setItem(furthestStepKey, String(currentStep));
    }
  }, [furthestStepKey, currentStep]);

  /**
   * Read rather than kept in state: the write above lands after this render, and a step is only
   * ever asked about up to the one currently open, so taking the larger of the two is enough and
   * saves a second render per navigation.
   */
  const furthestStep = (() => {
    if (isModuleMismatch) return currentStep;
    const stored = window.sessionStorage.getItem(furthestStepKey);
    if (stored === ALL_STEPS_REACHED) return STEPS.length - 1;
    const parsed = Number(stored);
    return Number.isFinite(parsed) ? Math.max(parsed, currentStep) : currentStep;
  })();

  /**
   * @function extractPath
   * @description
   * Extracts the base path from the current URL, removing the step parameter if present.
   * This is used to construct navigation paths for different steps in the acquisition process.
   * @param {boolean} hasStep - Indicates if the URL contains a step parameter
   * *Called by:
   * @see {navigate} - Function to navigate between steps
   * @see {navigateTo} - Function to navigate to a specific step
   *
   * @returns {string} The base path without the step parameter
   * @example
   * // Given URL: /acquisition/3 and hasStep = true
   * extractPath(true) // returns "/acquisition"
   *
   */
  const extractPath = (hasStep) => {
    return hasStep ? match.url.substring(0, match.url.lastIndexOf("/")) : match.url;
  };

  /**
   * @function navigate
   * @description
   * Navigates to a different step in the acquisition process based on the direction parameter.
   * Updates the URL to reflect the new step.
   * @param {number} direction - Direction to navigate: PREVIOUS (-1), NEXT (1), FIRST (0), FINALIZE (2)
   * *Called by:
   * @see {renderStep} - To navigate between steps
   * @returns {void}
   */
  const navigate = (direction) => {
    const path = extractPath(true);
    const { step } = match.params;
    switch (direction) {
      case NEXT:
      case PREVIOUS:
        dispatch(
          push(`${path}/${parseInt(step, 10) + direction}`),
          custoAlgorithm !== "" ? { algorithm: custoAlgorithm } : {}
        );
        break;
      case FIRST:
        dispatch(push(`${path}/1`), custoAlgorithm !== "" ? { algorithm: custoAlgorithm } : {});
        break;
      case FINALIZE:
        dispatch(push(resultPath), custoAlgorithm !== "" ? { algorithm: custoAlgorithm } : {});
        break;
      default:
        break;
    }
  };

  /**
   * @function navigateTo
   * @description
   * Navigates directly to a specific step updating the URL.
   * @param {number} path_index - Index of the step to navigate to (0-based)
   * @see {extractPath} - To get the base path for navigation
   * @returns {void}
   */
  const navigateTo = (path_index) => {
    const currentPath = extractPath(true);
    dispatch(push(`${currentPath}/${path_index + 1}`));
  };

  /**
   * @function renderStep
   * @description
   * Renders the component for the current step if it meets the requirements.
   * Passes navigation function and acquisition data as props to the step component.
   * @param {Object} props - Props to be passed to the step component
   * @param {React.Component} Component - The step component to render
   * *Called by:
   * @see {makeRoutes} - To render components for each route
   *
   * @returns {JSX.Element|null} The rendered step component or null if requirements are not met
   */
  const renderStep = (props, Component, stepIndex) => {
    const path = extractPath(true);
    if (stepAttendsRequirements()) {
      return (
        <Component {...props} lastStep={stepIndex === STEPS.length - 1} navigate={(direction) => navigate(direction)} />
      );
    }
    return null;
  };

  /**
   * @function stepAttendsRequirements
   * @description
   * Check if the step is valid and attends requirements
   * 1. Get the step from the url
   * 2. Set step variable to 0 (start)if undefined else set as current step - 1
   * 3. Get the step data from the STEPS array
   * 4. Check if the step has requirements
   * @returns {boolean} True if the step meets requirements, false otherwise
   */
  const stepAttendsRequirements = () => stepMeetsRequirements(currentStep);

  /**
   * @function stepMeetsRequirements
   * @description Whether one step's own `requires` field has a value in the acquisition state, and
   * so whether that step has what it needs to render.
   * Asks for a value and not just for the key: a reducer that declares its field up front - MEPBay's
   * starts as `satellite: null` - hands the selector a key that is present from the very first
   * render, so a check for the key alone reported the satellite step as satisfied before any
   * satellite had been chosen, and the step after it opened onto nothing.
   * @param {number} index - Index of the step in STEPS (0-based)
   * *Called by:
   * @see {stepAttendsRequirements} - for the step currently open
   * @see {stepIsAvailable} - for every step up to the one being asked about
   * @returns {boolean} True when the step's requirement is met, or it has none
   */
  const stepMeetsRequirements = (index) => {
    const stepData = STEPS[index];
    if (!stepData) return false;
    if (!("requires" in stepData)) return true;
    if (isModuleMismatch) return false;
    const value = acquisitionData[stepData.requires];
    if (value === undefined || value === null) return false;
    return !(Array.isArray(value) && value.length === 0);
  };

  /**
   * @function stepIsAvailable
   * @description Whether a step can be opened directly rather than only by walking the flow up to
   * it: true when that step and every step before it has its requirement met. That is the same
   * condition `renderStep` applies, so a step reported as available is one that will actually
   * render instead of rendering nothing and bouncing back to the first step.
   * This is what keeps an imported session navigable. The importer fills the acquisition state -
   * satellite, area, dates - in one go and drops the user straight on the result page, so the
   * steps were never walked through; asking what the data allows, rather than how far the user
   * got, is what lets them be reopened afterwards with their values in place.
   * @param {number} index - Index of the step in STEPS (0-based)
   * *Called by:
   * @see {canRevisitStep}
   * @returns {boolean} True when the step can be navigated to directly
   */
  const stepIsAvailable = (index) => {
    if (index < 0 || index >= STEPS.length) return false;
    for (let i = 0; i <= index; i += 1) {
      if (!stepMeetsRequirements(i)) return false;
    }
    return true;
  };

  /**
   * @function canRevisitStep
   * @description Whether the step indicator may jump to a step. The step currently open and any
   * step behind it always qualify - their data is, by definition, in place. Ahead of it, a step has
   * to have been reached before *and* be satisfied by the data gathered so far, which is what keeps
   * a session restored from a JSON import fully navigable while leaving a flow that has not got
   * there yet closed: every step used to be a live button, so clicking ahead pushed the route to a
   * step whose data was not gathered, and the mount check then threw the user back to step one.
   * Shared by all eight wizards so there is one answer to this question rather than one per module.
   * @param {number} index - Index of the step in STEPS (0-based)
   * *Called by:
   * @see {AcquisitionPage} - each module's wizard, through its indicator
   * @returns {boolean} True when the step may be jumped to
   */
  const canRevisitStep = (index) => {
    if (index < 0 || index >= STEPS.length) return false;
    if (index <= currentStep) return true;
    return index <= furthestStep && stepIsAvailable(index);
  };

  /**
   * @function makeRoutes
   * @description
   * Generates route components for each step in the acquisition process.
   * @param {boolean} hasStep - Indicates if the URL contains a step parameter
   * @see {extractPath} - To get the base path for routes
   * @see {renderStep} - To render components for each route
   *
   * @returns {JSX.Element[]} Array of Route components for each step
   */
  const makeRoutes = (hasStep, additionalProps) => {
    const path = extractPath(hasStep);
    return STEPS.map((el, i) => {
      const id = i + 1;
      const Component = el.component;
      return (
        <Route
          key={id}
          path={`${path}/${id}`}
          render={(props) => renderStep({ ...props, ...additionalProps, ...acquisitionData }, Component, i)}
        />
      );
    }).concat(<Redirect key={0} from={path} to={path + "/1"} />);
  };

  return {
    acquisitionData,
    navigate,
    navigateTo,
    renderStep,
    extractPath,
    stepAttendsRequirements,
    stepIsAvailable,
    canRevisitStep,
    makeRoutes,
  };
};
